/**
 * Activity modes (data/modes.ts): the player's pick of vs NPCs (สู้กับ NPC) or vs players (สู้กับผู้เล่น) for each
 * activity, remembered until changed, and the one place that turns a pick into the mode an activity runs in.
 * Players found is a stub: offline nobody is, so `pvp` falls back to `npc` with a notice. A server sets
 * `pvpPool` (players waiting per activity) and swaps in its own matching; the rules read `resolve()` only.
 * No dice are drawn here, so nothing changes in the World's random stream.
 */
import {
  ACTIVITY_MODES,
  isActivityMode,
  isModeActivity,
  MODE_ACTIVITIES,
  resolveMode,
  type ActivityMode,
  type ModeActivityId,
} from '../data/modes';
import type { World } from '../world';
import type { SystemHost, WorldSystem } from './types';

export type ModesCommand = { t: 'activityMode'; activity: ModeActivityId; mode: ActivityMode };
export type ModesEvent = {
  type: 'activityMode';
  activity: ModeActivityId;
  mode: ActivityMode;
  /** True when `pvp` was asked and no players were found. */
  fellBack: boolean;
};

declare module '../world' {
  interface Duel {
    /** The mode this bout was signed up in (data/modes.ts); absent = `npc`. */
    mode?: ActivityMode;
  }
}

const SYSTEMS = new WeakMap<object, ModesSystem>();

export class ModesSystem implements WorldSystem {
  readonly name = 'modes';
  /** The player's pick per activity (default `npc`). */
  readonly picked = new Map<ModeActivityId, ActivityMode>();
  /** Players waiting per activity; set by the server (ONLINE.md). Offline it stays empty. */
  readonly pvpPool = new Map<ModeActivityId, number>();

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  readonly commands = {
    activityMode: (c: { activity: ModeActivityId; mode: ActivityMode }) => {
      if (!isModeActivity(c.activity) || !isActivityMode(c.mode)) return;
      if (!(MODE_ACTIVITIES[c.activity].modes as readonly ActivityMode[]).includes(c.mode)) return;
      this.picked.set(c.activity, c.mode);
      this.h.notice(`${MODE_ACTIVITIES[c.activity].label}: ${ACTIVITY_MODES[c.mode].label}`);
    },
  };

  /** The pick for an activity. */
  pick(activity: ModeActivityId): ActivityMode {
    return this.picked.get(activity) ?? 'npc';
  }

  /** Players found for an activity (a stub: the server's pool). */
  pvpFound(activity: ModeActivityId): boolean {
    return (this.pvpPool.get(activity) ?? 0) > 0;
  }

  /**
   * The mode an activity runs in now: `asked` (the sign-up's choice) or else the remembered pick; `pvp` with no
   * players found falls back to `npc`. Says so (notice + `activityMode` event) only when it fell back.
   */
  resolve(activity: ModeActivityId, asked?: ActivityMode, quiet = false): ActivityMode {
    const r = resolveMode(asked ?? this.pick(activity), MODE_ACTIVITIES[activity].modes, this.pvpFound(activity));
    // Said only when pvp fell back (a plain npc sign-up changes nothing the client must hear).
    if (!quiet && r.fellBack) {
      this.h.notice('ไม่พบผู้เล่น สู้กับ NPC แทน');
      this.h.emit({ type: 'activityMode', activity, mode: r.mode, fellBack: true });
    }
    return r.mode;
  }
}

export const modesOf = (w: World): ModesSystem | null => SYSTEMS.get(w) ?? null;

/** The mode `activity` would run in, with no event and no notice (null before the system exists). */
export const modeNow = (w: World, activity: ModeActivityId, asked?: ActivityMode): ActivityMode =>
  modesOf(w)?.resolve(activity, asked, true) ?? 'npc';
